import React from "react";
import { Link } from "react-router-dom";
import { Page } from "@/components/Layout";
import { useConsultModal } from "@/components/ConsultModal";
import { ArrowRight, Brain, Compass, Question } from "@phosphor-icons/react";
import Seo from "@/components/Seo";
import JsonLd from "@/components/JsonLd";

const paths = [
  { icon: Brain, title: "General Support", desc: "Browse therapists and book directly.", to: "/therapists", cta: "Browse Therapists" },
  { icon: Compass, title: "Cognitive & Neuropsych Support", desc: "Founder-led Structured Pathway with Shreeja Reddy.", to: "/couch/cognitive-support", cta: "Start Structured Pathway" },
  { icon: Question, title: "Not Sure", desc: "Book a free 20-min consultation with Shreeja.", action: "consult", cta: "Book a Free Consultation" },
];

export default function CouchLanding() {
  const { open: openConsult } = useConsultModal();
  return (
    <Page>
      <JsonLd
        id="jsonld-service-couch"
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          name: "MindDhara Couch",
          description: "Therapy, cognitive support and mental wellbeing sessions with globally trained, culturally competent psychologists.",
          url: "https://minddhara.com/couch",
          provider: { "@type": "Organization", name: "MindDhara", url: "https://minddhara.com" },
          areaServed: "Worldwide",
        }}
      />
      <Seo title="MindDhara Couch · Therapy & Mental Wellbeing" description="Therapy, cognitive support and mental wellbeing with globally trained, culturally competent practitioners. Book directly, no waitlists." />
      <section className="flow-gradient md-section md-page-hero" data-testid="couch-hero">
        <div className="md-container">
          <div className="md-label-eyebrow mb-4"><span className="md-brand">MindDhara</span> Couch</div>
          <h1 className="text-5xl md:text-6xl font-serif mb-5 max-w-3xl">Care for your mind, with people who get it</h1>
          <p className="text-lg max-w-2xl" style={{ color: "var(--md-text-soft)" }}>Mental health, cognitive health and everyday functioning, three entry paths, your pace.</p>
          <div className="flex flex-wrap gap-3 mt-8">
            <Link to="/therapists?source=couch-hero" className="btn-primary" data-testid="couch-hero-browse-therapists">Browse Therapists <ArrowRight size={14} weight="bold" /></Link>
            <Link to="/couch/cognitive-support?source=couch-hero" className="btn-outline" data-testid="couch-hero-structured-pathway">Start Structured Pathway</Link>
            <button onClick={openConsult} className="btn-outline" data-testid="couch-hero-free-consultation">Book a Free Consultation</button>
          </div>
        </div>
      </section>

      <section className="md-section">
        <div className="md-container">
          <div className="grid md:grid-cols-3 gap-6">
            {paths.map((p) => {
              const testid = `couch-path-${p.title.toLowerCase().replace(/\s+/g, "-")}`;
              const inner = (
                <>
                  <div className="how-glass-ico" style={{ background: "var(--md-bg-alt)", border: "1px solid var(--md-border)" }}>
                    <p.icon size={26} weight="duotone" style={{ color: "var(--md-primary)" }} />
                  </div>
                  <h3 className="text-2xl font-serif mt-2">{p.title}</h3>
                  <p className="mt-3 text-sm" style={{ color: "var(--md-text-soft)" }}>{p.desc}</p>
                  <div className="mt-6 inline-flex items-center gap-1 text-sm font-medium" style={{ color: "var(--md-primary)" }}>{p.cta} <ArrowRight size={14} weight="bold" /></div>
                </>
              );
              if (p.action === "consult") {
                return (
                  <button key={p.title} onClick={openConsult} className="md-card md-card-hover text-left" data-testid={testid}>{inner}</button>
                );
              }
              return (
                <Link key={p.title} to={p.to} className="md-card md-card-hover" data-testid={testid}>{inner}</Link>
              );
            })}
          </div>
        </div>
      </section>
    </Page>
  );
}
